import { ErrorCode } from "@shuttlerock/api-types/federated";
import { AccessDeniedError, NotFoundError } from "@shuttlerock/shell-components";
import { isRouteErrorResponse } from "react-router";
import { asRouteErrorResponse } from "../../util/loader-query-failure";
import { hasApolloErrorCode } from "../../util/mapApolloErrors";
import type { AccessDeniedData, RouteErrorClassification } from "./types";

function isResponseStatus(error: unknown, status: number | number[]): boolean {
  if (!isRouteErrorResponse(error)) {
    return false;
  }
  const statuses = Array.isArray(status) ? status : [status];
  return statuses.includes(error.status);
}

/**
 * The payload a loader/action threw with `data(payload, status)` — the only
 * error shape that carries caller-authored content for the boundary. Nothing
 * else is read: an Apollo error's `data` is the GraphQL response payload, and
 * lifting it here would render server data as page copy.
 */
function getRouteResponseData(error: unknown): unknown {
  return isRouteErrorResponse(error) ? error.data : undefined;
}

/**
 * Classifies an error thrown from a route loader/action/render into the kind of
 * page that should be shown. Shared by every route error boundary so the same
 * rules apply at the app root and inside feature routes.
 *
 * Entity-withheld denials (`FORBIDDEN` beside a key-only stub in the data —
 * see `util/entity-withheld`) are deliberately not stripped here: the verdict
 * keys on whole-operation codes, which the withheld code is by design not one
 * of, so such errors can neither earn nor mask a page. They reach the boundary
 * only when a loader ran its query under the default `errorPolicy` instead of
 * through `loaderQuery` (or `requestRecoveringWithheldEntities` for
 * graphql-request): the client rejects with a `CombinedGraphQLErrors` carrying
 * the partial, a whole-operation code on it still classifies as usual, and a
 * withheld-only rejection is a crash — loud and reported, because the boundary
 * cannot resurrect the route's data.
 */
export function classifyRouteError(thrown: unknown): RouteErrorClassification {
  // A `data(…, status)` response thrown from render (a deferred loader promise
  // read with `use()`) classifies as the ErrorResponse a loader's throw becomes.
  const error = asRouteErrorResponse(thrown);
  if (isResponseStatus(error, 401) && getRouteResponseData(error) === "login_required") {
    return { type: "loginRequired" };
  }

  if (
    error instanceof AccessDeniedError ||
    isResponseStatus(error, [401, 403]) ||
    hasApolloErrorCode(error, ErrorCode.Unauthorised) ||
    hasApolloErrorCode(error, ErrorCode.NotAuthorised)
  ) {
    const data = getRouteResponseData(error);
    return {
      type: "accessDenied",
      data: typeof data === "object" && data !== null ? (data as AccessDeniedData) : undefined,
    };
  }

  if (
    error instanceof NotFoundError ||
    isResponseStatus(error, 404) ||
    hasApolloErrorCode(error, ErrorCode.NotFound)
  ) {
    return { type: "notFound" };
  }

  if (isResponseStatus(error, 410)) {
    const data = getRouteResponseData(error);
    return { type: "gone", message: typeof data === "string" ? data : undefined };
  }

  return { type: "crash" };
}

/**
 * Whether a route boundary renders the error as a page of its own (login,
 * access denied, not found, gone) rather than as a crash. Such an error is
 * expected: the boundary does not report it, and nothing should log it.
 */
export function isExpectedRouteError(thrown: unknown): boolean {
  return classifyRouteError(thrown).type !== "crash";
}
